import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { auth, db, functions } from '../firebase';
import { collection, getDocs, doc, runTransaction, writeBatch, query, orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { buildEventFields, fetchStrictActiveEvent } from '../utils/events';
import { syncQrIndexForOrder } from '../utils/qrIndex';
import { forceSignOutStaff } from '../utils/staffSessionGuard';
import { QRCode } from 'react-qr-code';
import Spinner from './Spinner';
import './LocalSales.css';

const formatNumber = (number) => {
  if (typeof number !== 'number' || isNaN(number)) {
    return '0,00';
  }
  return number
    .toFixed(2) // Mantiene dos decimales
    .replace('.', ',') // Reemplaza el punto decimal por una coma
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.'); // Agrega puntos como separadores de miles
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const createSHA256Hash = async (input) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

const LocalSales = () => {
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [customerSearchFocused, setCustomerSearchFocused] = useState(false);
  const [customerData, setCustomerData] = useState({
    name: '',
    cuit: '',
    invoiceType: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    province: '',
    country: '',
    additionalInfo: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [saleInfo, setSaleInfo] = useState(null);
  const [deliveryEmail, setDeliveryEmail] = useState('');
  const [deliveryMessage, setDeliveryMessage] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const [ticketsCollection, consumptionsCollection] = await Promise.all([
        getDocs(collection(db, 'products')),
        getDocs(collection(db, 'otherProducts')),
      ]);
      const ticketsData = ticketsCollection.docs.map(productDoc => ({
        ...productDoc.data(),
        id: productDoc.id,
        collection: 'products',
        stock: Number(productDoc.data().stock) || 0
      }));
      const consumptionsData = consumptionsCollection.docs.map(productDoc => ({
        ...productDoc.data(),
        id: productDoc.id,
        collection: 'otherProducts',
        stock: Number(productDoc.data().stock) || 0
      }));
      setProducts([...ticketsData, ...consumptionsData].sort((a, b) => a.model.localeCompare(b.model)));
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const fetchCustomers = useCallback(async () => {
    try {
      const q = query(collection(db, 'ventas_fisicas'), orderBy('date', 'desc'), limit(300));
      const snapshot = await getDocs(q);
      const customersByName = new Map();

      snapshot.docs.forEach((saleDoc) => {
        const sale = saleDoc.data();
        const data = sale.customerData || {};
        const name = String(data.name || '').trim();
        if (!name) return;

        const key = name.toLowerCase();
        if (!customersByName.has(key)) {
          customersByName.set(key, {
            id: saleDoc.id,
            name,
            email: String(data.email || '').trim(),
            data,
          });
        }
      });

      setCustomers([...customersByName.values()].sort((a, b) => a.name.localeCompare(b.name, 'es')));
    } catch (error) {
      console.error('Error fetching local sale customers:', error);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleKeyPress = (event, product, quantity) => {
    if (event.key === "Enter" || event.key === "Tab") {
      addProductToSale(product, quantity);
    }
  };

  const addProductToSale = (product, quantity) => {
    const parsedQuantity = Number(quantity);
    if (parsedQuantity <= 0 || isNaN(parsedQuantity)) {
      return;
    }

    setSelectedProducts(prevSelectedProducts => {
      const existingProduct = prevSelectedProducts.find(item =>
        item.id === product.id && item.collection === product.collection
      );

      if (existingProduct) {
        return prevSelectedProducts.map(item =>
          item.id === product.id && item.collection === product.collection
            ? { ...item, quantity: existingProduct.quantity + parsedQuantity }
            : item
        );
      } else {
        return [...prevSelectedProducts, { ...product, quantity: parsedQuantity }];
      }
    });
  };


  const removeProductFromSale = (productId, collectionName) => {
    setSelectedProducts(prevSelectedProducts =>
      prevSelectedProducts.filter(item => !(item.id === productId && item.collection === collectionName))
    );
  };

  const ensureCurrentStaffCanSell = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      await forceSignOutStaff('session');
      return false;
    }

    try {
      await user.getIdToken(true);
      const profileFn = httpsCallable(functions, 'staffGetOwnProfile');
      const res = await profileFn({});
      const profile = res.data || {};
      const role = String(profile.role || '').toLowerCase();
      const allowedFeatures = Array.isArray(profile.allowedFeatures) ? profile.allowedFeatures : [];
      const canSell =
        profile.exists === true &&
        profile.authDisabled !== true &&
        (role === 'admin' || role === 'cashier' || (role === 'staff' && allowedFeatures.includes('localSales')));

      if (canSell) {
        return true;
      }

      await forceSignOutStaff(profile.authDisabled === true ? 'disabled' : 'permission');
      return false;
    } catch (error) {
      const code = error?.code || '';
      const shouldSignOut =
        code === 'functions/unauthenticated' ||
        code === 'unauthenticated' ||
        code === 'functions/permission-denied' ||
        code === 'permission-denied';

      if (shouldSignOut) {
        await forceSignOutStaff(code.includes('unauthenticated') ? 'session' : 'permission');
        return false;
      }

      console.error('Error validating local sale staff session:', error);
      alert('No se pudo verificar si tu usuario sigue habilitado. Volvé a intentar.');
      return false;
    }
  }, []);

  const getOrderNumber = async () => {
    const counterRef = doc(db, 'counters', 'fisicalCounter');
    try {
      const newOrderNumber = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);
        if (!counterDoc.exists()) {
          throw new Error('Fisical counter document does not exist!');
        }
        const newOrderNumber = counterDoc.data().currentOrderNumber + 1;
        transaction.update(counterRef, { currentOrderNumber: newOrderNumber });
        return newOrderNumber;
      });
      return newOrderNumber;
    } catch (error) {
      console.error("Transaction failed: ", error);
      alert('Error generando número de orden, por favor intente nuevamente.');
      return null;
    }
  };

  const buildQrCartItems = async (saleProducts, orderNumber) => {
    const customerName = customerData.name.trim() || 'Cliente local';
    const cartItems = [];

    for (const product of saleProducts) {
      const quantity = Math.max(1, Number(product.quantity) || 1);
      for (let unit = 1; unit <= quantity; unit += 1) {
        const uniqueSeed = `${product.collection}:${product.id}:${orderNumber}:${unit}:${Date.now()}:${Math.random()}`;
        cartItems.push({
          id: product.id,
          collection: product.collection,
          model: product.model,
          price: Number(product.price) || 0,
          quantity: 1,
          eventId: product.eventId || '',
          eventName: product.eventName || '',
          eventDate: product.eventDate || '',
          qrValue: await createSHA256Hash(uniqueSeed),
          isUsed: false,
          nombre: customerName,
          recipientEmail: customerData.email || '',
          source: 'localSale',
          originalQuantity: quantity,
          unitNumber: unit,
        });
      }
    }

    return cartItems;
  };

  const getSaleCreatorAudit = () => {
    const user = auth.currentUser;
    let role = '';
    try {
      role = localStorage.getItem('userRole') || '';
    } catch (_) {
      role = '';
    }

    return {
      createdByUid: user?.uid || null,
      createdByEmail: user?.email || null,
      createdByName: user?.displayName || user?.email || null,
      createdByRole: role || null,
    };
  };

  const handleSaleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const canSell = await ensureCurrentStaffCanSell();
    if (!canSell) {
      setIsSubmitting(false);
      return;
    }

    let activeEvent = null;
    try {
      activeEvent = await fetchStrictActiveEvent(db);
    } catch (error) {
      console.error('Error fetching active event:', error);
    }

    if (!activeEvent) {
      alert('No hay un evento activo. Activá un evento antes de generar QR.');
      setIsSubmitting(false);
      return;
    }

    const inactiveEventProduct = selectedProducts.find((product) => (
      product.eventId && product.eventId !== activeEvent.id
    ));
    if (inactiveEventProduct) {
      alert(`No se puede generar QR para "${inactiveEventProduct.model}" porque pertenece a un evento desactivado.`);
      setIsSubmitting(false);
      return;
    }

    const orderNumber = await getOrderNumber();
    if (!orderNumber) {
      setIsSubmitting(false);
      return;
    }

    try {
      const batch = writeBatch(db);

      const saleProducts = selectedProducts.map((product) => {
        const eventSource = product.collection === 'otherProducts'
          ? activeEvent
          : (product.eventId ? product : activeEvent);

        return {
          ...product,
          ...buildEventFields(eventSource),
        };
      });

      const cartItems = await buildQrCartItems(saleProducts, orderNumber);
      const saleData = {
        orderNumber,
        date: new Date(),
        products: saleProducts,
        cartItems,
        customerData,
        totalPrice: parseFloat(calculateTotal(saleProducts)),
        isConfirmed: true,
        isPaid: true,
        ...getSaleCreatorAudit(),
        ...buildEventFields(activeEvent),
      };

      const saleRef = doc(collection(db, 'ventas_fisicas'));
      batch.set(saleRef, saleData);

      saleProducts.forEach((product) => {
        if (product.excludeFromStock) return;
        const productRef = doc(db, product.collection || 'products', product.id);
        const newStock = product.stock - product.quantity;
        batch.update(productRef, { stock: newStock });
      });

      await batch.commit();
      try {
        await syncQrIndexForOrder(db, 'ventas_fisicas', saleRef.id, cartItems);
      } catch (indexError) {
        console.error('Error syncing local sale QR index:', indexError);
      }
      const createdSale = { ...saleData, id: saleRef.id };
      setSaleInfo(createdSale);
      setDeliveryEmail(customerData.email || '');
      setDeliveryMessage('');
      setShowModal(true);
    } catch (error) {
      console.error("Error registering sale:", error);
      alert('Error al registrar la venta. Por favor, intente nuevamente.');
    } finally {
      setIsSubmitting(false);
      fetchProducts();
      fetchCustomers();
    }
  };

  const calculateTotal = useCallback((products) => {
    return products.reduce((total, item) => {
      const itemTotal = parseFloat(item.price) * parseInt(item.quantity, 10);
      return total + (isNaN(itemTotal) ? 0 : itemTotal);
    }, 0);
  }, []);

  const resetComponent = () => {
    setSelectedProducts([]);
    setCustomerData({
      name: '',
      cuit: '',
      invoiceType: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      postalCode: '',
      province: '',
      country: '',
      additionalInfo: '',
    });
    setShowAdditionalFields(false);
    setShowModal(false);
    setSaleInfo(null);
    setDeliveryEmail('');
    setDeliveryMessage('');
    setDeliveryStatus('');
    setIsSendingEmail(false);
  };

  const filteredProducts = searchTerm
    ? products.filter(product =>
      product.model.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 5)
    : [];

  const customerSuggestions = useMemo(() => {
    const search = customerData.name.trim().toLowerCase();
    if (!search) return [];
    return customers
      .filter((customer) => customer.name.toLowerCase().includes(search))
      .slice(0, 5);
  }, [customerData.name, customers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleSelectCustomer = (customer) => {
    setCustomerData(prevData => ({
      ...prevData,
      ...customer.data,
      name: customer.name,
      email: customer.email || customer.data?.email || '',
    }));
    setCustomerSearchFocused(false);
    if (customer.email) {
      setDeliveryEmail(customer.email);
    }
  };

  const handlePrintQrs = () => {
    window.print();
  };

  const handleEmailQrs = async () => {
    if (!saleInfo?.id || !saleInfo?.cartItems?.length || isSendingEmail) return;

    const targetEmail = deliveryEmail.trim();
    if (!EMAIL_REGEX.test(targetEmail)) {
      setDeliveryStatus('error');
      setDeliveryMessage('Ingresá un email válido para enviar los QR.');
      return;
    }

    setIsSendingEmail(true);
    setDeliveryMessage('');
    setDeliveryStatus('');
    try {
      const canSell = await ensureCurrentStaffCanSell();
      if (!canSell) return;

      const sendLocalSaleQrEmail = httpsCallable(functions, 'sendLocalSaleQrEmail');
      await sendLocalSaleQrEmail({
        saleId: saleInfo.id,
        email: targetEmail,
      });
      setSaleInfo(prevSale => prevSale
        ? {
            ...prevSale,
            customerData: {
              ...(prevSale.customerData || {}),
              email: targetEmail,
            },
            localSaleQrEmailSent: true,
          }
        : prevSale);
      setCustomerData(prevData => ({ ...prevData, email: targetEmail }));
      setDeliveryStatus('success');
      setDeliveryMessage(targetEmail);
    } catch (error) {
      console.error('Error sending local sale QR email:', error);
      setDeliveryStatus('error');
      setDeliveryMessage(error?.message || 'No se pudo enviar el email con los QR.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="local-sales">
      <h2 className="local-sales-title">Registrar Venta Física</h2>
      <input
        type="text"
        placeholder="Buscar producto"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="local-sales-search-input"
      />
      <div className="local-sales-products-list">
        {filteredProducts.map(product => (
          <div key={`${product.collection}:${product.id}`} className="local-sales-product-item">
            <img src={product.images ? product.images[0] : ''} alt={product.model} className="local-sales-product-image" />
            <div className="local-sales-product-info">
              <span>{product.model} - ${formatNumber(product.price)}</span>
              <span className="local-sales-stock-info">
                {product.collection === 'otherProducts' ? 'Consumo' : 'Entrada'} · En stock: {product.stock}
              </span>
              <input
                type="number"
                onKeyDown={(e) => handleKeyPress(e, product, e.target.value)}
                placeholder="Ingrese cantidad"
                className="local-sales-quantity-input"
              />

            </div>
          </div>
        ))}
      </div>
      <h3 className="local-sales-selected-title">Productos Seleccionados</h3>
      <ul className="local-sales-selected-list">
        {selectedProducts.map(product => (
          <li key={`${product.collection}:${product.id}`} className="local-sales-selected-item">
            <span>{product.model} - {product.quantity} unidades · {product.collection === 'otherProducts' ? 'Consumo' : 'Entrada'}</span>
            <div className="local-sales-selected-item-actions">
              <span className="local-sales-unit-price">${formatNumber(Number(product.price) || 0)} c/u</span>
              <span className="local-sales-product-total">${formatNumber(product.price * product.quantity)}</span>
              <button onClick={() => removeProductFromSale(product.id, product.collection)} className="local-sales-remove-btn">Eliminar</button>
            </div>
          </li>
        ))}
      </ul>
      <h4 className="local-sales-total">Total: ${formatNumber(calculateTotal(selectedProducts))}</h4>
      <div className="local-sales-customer-info">
        <h3>Datos del Cliente</h3>
        <div className="local-sales-customer-search">
          <input
            type="text"
            name="name"
            placeholder="Nombre del Cliente"
            value={customerData.name}
            onChange={handleInputChange}
            onFocus={() => setCustomerSearchFocused(true)}
            className="local-sales-customer-input"
            autoComplete="off"
          />
          {customerSearchFocused && customerSuggestions.length > 0 && (
            <div className="local-sales-customer-suggestions">
              {customerSuggestions.map((customer) => (
                <button
                  type="button"
                  key={customer.id}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    handleSelectCustomer(customer);
                  }}
                  className="local-sales-customer-suggestion"
                >
                  <strong>{customer.name}</strong>
                  <span>{customer.email || 'Sin email registrado'}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {customerData.email && (
          <p className="local-sales-customer-hint">
            Email cargado: <strong>{customerData.email}</strong>
          </p>
        )}
        <button onClick={() => setShowAdditionalFields(!showAdditionalFields)} className="local-sales-additional-fields-btn">
          {showAdditionalFields ? 'Ocultar datos adicionales' : 'Agregar datos de cliente'}
        </button>
        {showAdditionalFields && (
          <>
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={customerData.email}
              onChange={handleInputChange}
              className="local-sales-customer-input"
            />
            <input
              type="text"
              name="cuit"
              placeholder="CUIT"
              value={customerData.cuit}
              onChange={handleInputChange}
              className="local-sales-customer-input"
            />
            <input
              type="text"
              name="invoiceType"
              placeholder="Tipo de Factura"
              value={customerData.invoiceType}
              onChange={handleInputChange}
              className="local-sales-customer-input"
            />
            <input
              type="text"
              name="phone"
              placeholder="Teléfono"
              value={customerData.phone}
              onChange={handleInputChange}
              className="local-sales-customer-input"
            />
            <input
              type="text"
              name="address"
              placeholder="Dirección"
              value={customerData.address}
              onChange={handleInputChange}
              className="local-sales-customer-input"
            />
            <input
              type="text"
              name="city"
              placeholder="Ciudad"
              value={customerData.city}
              onChange={handleInputChange}
              className="local-sales-customer-input"
            />
            <input
              type="text"
              name="postalCode"
              placeholder="Código Postal"
              value={customerData.postalCode}
              onChange={handleInputChange}
              className="local-sales-customer-input"
            />
            <input
              type="text"
              name="province"
              placeholder="Provincia"
              value={customerData.province}
              onChange={handleInputChange}
              className="local-sales-customer-input"
            />
            <input
              type="text"
              name="country"
              placeholder="País"
              value={customerData.country}
              onChange={handleInputChange}
              className="local-sales-customer-input"
            />
            <textarea
              name="additionalInfo"
              placeholder="Información Adicional"
              value={customerData.additionalInfo}
              onChange={handleInputChange}
              className="local-sales-customer-input"
            />
          </>
        )}
      </div>
      <div className="local-sales-submit-container">
        <button
          onClick={handleSaleSubmit}
          className={`local-sales-submit-btn ${isSubmitting ? 'local-sales-submit-btn-disabled' : ''}`}
          disabled={isSubmitting || selectedProducts.length === 0}
        >
          {isSubmitting ? 'Registrando venta...' : 'Registrar venta y generar QR'}
        </button>
        {isSubmitting && <Spinner />}
      </div>
      {showModal && saleInfo && (
        <div className="local-sales-modal">
          <div className="local-sales-modal-content">
            <div className="local-sales-modal-header">
              <div>
                <span className="local-sales-modal-eyebrow">Venta registrada</span>
                <h2>QR listos para entregar</h2>
              </div>
              <span className="local-sales-order-pill">Orden #{saleInfo.orderNumber}</span>
            </div>

            <div className="local-sales-sale-summary">
              <span>Total: <strong>${formatNumber(saleInfo.totalPrice)}</strong></span>
              <span>Cliente: <strong>{saleInfo.customerData?.name || 'Sin nombre'}</strong></span>
              <span>QR generados: <strong>{saleInfo.cartItems?.length || 0}</strong></span>
            </div>

            <div className="local-sales-delivery-actions">
              <label className="local-sales-delivery-email">
                <span>Email para enviar QR</span>
                <input
                  type="email"
                  value={deliveryEmail}
                  onChange={(event) => {
                    setDeliveryEmail(event.target.value);
                    setDeliveryMessage('');
                    setDeliveryStatus('');
                  }}
                  placeholder="cliente@email.com"
                  className="local-sales-customer-input"
                />
              </label>
              <div className="local-sales-delivery-buttons">
                <button
                  type="button"
                  onClick={handleEmailQrs}
                  className="local-sales-modal-action"
                  disabled={isSendingEmail}
                >
                  {isSendingEmail ? 'Enviando...' : 'Enviar por email'}
                </button>
                <button type="button" onClick={handlePrintQrs} className="local-sales-modal-action local-sales-modal-action-secondary">
                  Imprimir QR
                </button>
              </div>
            </div>
            {deliveryMessage && (
              <div
                className={`local-sales-delivery-message local-sales-delivery-message-${deliveryStatus || 'info'}`}
                role="status"
                aria-live="polite"
              >
                {deliveryStatus === 'success' ? (
                  <>
                    <strong>Email enviado correctamente</strong>
                    <span>Los QR de esta venta ya fueron enviados a:</span>
                    <b>{deliveryMessage}</b>
                    <small>El cliente puede abrir el correo y mostrar los códigos desde su teléfono.</small>
                  </>
                ) : (
                  <>
                    <strong>No se pudo enviar el email</strong>
                    <span>{deliveryMessage}</span>
                  </>
                )}
              </div>
            )}

            <div className="local-sales-print-area">
              <h3>Tickets QR</h3>
              <div className="local-sales-qr-grid">
                {(saleInfo.cartItems || []).map((item, index) => (
                  <article key={item.qrValue || index} className="local-sales-qr-card">
                    <div className="local-sales-qr-code">
                      <QRCode
                        value={item.qrValue}
                        size={156}
                        style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                      />
                    </div>
                    <div className="local-sales-qr-meta">
                      <strong>{item.model}</strong>
                      <span>{saleInfo.customerData?.name || item.nombre || 'Cliente local'}</span>
                      <small>Orden #{saleInfo.orderNumber} · QR {index + 1}</small>
                      <small>{item.eventName || 'Allegra Club'}</small>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <button onClick={resetComponent} className="local-sales-modal-close">Finalizar</button>
          </div>
        </div>
      )}

    </div>
  );
};

export default LocalSales;
